import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaEdit, FaTrash, FaCopy } from 'react-icons/fa';
import { apiFetch } from '../api';
import { useAuth } from '../auth/AuthContext';
import { STAGE_GROUPS, sortByPriority, canManageContracts } from '../auth/roles';
import ContractEditModal from './ContractEditModal';
import ContractCreateModal from './ContractCreateModal'; // добавлен импорт
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { PageHeader, ProgressBar, QuantityProgress, DeadlineBadge, StageBadge } from './ui';
import { formatDate } from '../utils/date';
import ContractFiles from './ContractFiles';

function ContractsList({ filterGroup }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [editingContract, setEditingContract] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copyContractData, setCopyContractData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = canManageContracts(user?.role);

  const loadContracts = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page_size: '100' });
    if (filterGroup) params.set('stage_group', filterGroup);
    return apiFetch(`/api/contracts/?${params}`)
      .then((data) => {
        setContracts(data.results || data);
        setRefreshKey(Date.now());
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Не удалось загрузить список договоров');
        setLoading(false);
      });
  }, [filterGroup]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  const filtered = sortByPriority(
    contracts.filter((c) => {
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (
        (c.number || '').toLowerCase().includes(q) ||
        (c.product_name || '').toLowerCase().includes(q)
      );
    })
  );

  const groupInfo = filterGroup ? STAGE_GROUPS[filterGroup] : null;
  const title = groupInfo ? `${groupInfo.icon} ${groupInfo.label}` : 'Договоры';
  const subtitle = groupInfo
    ? `Договоры на текущей стадии «${groupInfo.label}»`
    : 'Все договоры по всем изделиям';

  const overdueCount = filtered.filter((c) => c.is_overdue).length;

  // Обработчик копирования
  const handleCopyContract = (contract) => {
    setCopyContractData(contract);
    setShowCreateModal(true);
  };

  // Обработчик успешного создания (после копирования или создания)
  const handleContractCreated = () => {
    loadContracts(true);
    setShowCreateModal(false);
    setCopyContractData(null);
  };

  if (loading) return <div className="empty-state">Загрузка...</div>;
  if (error) return <div className="empty-state text-danger">{error}</div>;

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-card-label">Договоров</div>
          <div className="stat-card-value">{filtered.length}</div>
        </div>
        {overdueCount > 0 && (
          <div className="stat-card" style={{ borderColor: 'var(--accent-red)' }}>
            <div className="stat-card-label priority-overdue">Просрочено</div>
            <div className="stat-card-value priority-overdue">{overdueCount}</div>
          </div>
        )}
      </div>

      <div className="search-box">
        <FaSearch className="app-text-muted" />
        <input
          placeholder="Поиск по номеру или изделию"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="contracts-list">
        {filtered.map((contract) => (
          <div
            key={contract.id}
            className="contract-card-full"
            onClick={() => navigate(`/contract/${contract.id}/stages`)}
            style={{ cursor: 'pointer' }}
          >
            <div className="contract-card-body">
              {/* Ряд 1: Договор, Стадия, Прогресс, Дедлайн */}
              <div className="contract-card-row contract-card-row-1">
                <div className="contract-card-field contract-card-field-number">
                  <div className="contract-card-label">Договор</div>
                  <div className="contract-card-value">{contract.number}</div>
                </div>
                <div className="contract-card-field contract-card-field-status">
                  <div className="contract-card-label">Стадия</div>
                  <StageBadge stage={contract.current_stage} />
                </div>
                <div className="contract-card-field contract-card-field-progress">
                  <div className="contract-card-label">Прогресс</div>
                  <div className="contract-card-value">{contract.progress}%</div>
                  <ProgressBar value={contract.progress || 0} size="sm" className="mt-1" />
                </div>
                {contract.nearest_deadline && (
                  <div className="contract-card-field contract-card-field-deadline">
                    <DeadlineBadge deadline={formatDate(contract.nearest_deadline)} isOverdue={contract.is_overdue} />
                  </div>
                )}
              </div>

              {/* Ряд 2: Период, Сумма, Кол-во, Файлы */}
              <div className="contract-card-row contract-card-row-2">
                <div className="contract-card-field contract-card-field-dates">
                  <div className="contract-card-label">Период</div>
                  <div className="contract-card-value">{formatDate(contract.start_date)} — {formatDate(contract.end_date)}</div>
                </div>
                <div className="contract-card-field contract-card-field-amount">
                  <div className="contract-card-label">Сумма</div>
                  <div className="contract-card-value">{Number(contract.amount).toLocaleString('ru-RU')} ₽</div>
                </div>
                <div className="contract-card-field contract-card-field-quantity">
                  <div className="contract-card-label">Кол-во</div>
                  <QuantityProgress done={contract.done_quantity || 0} total={contract.quantity || 0} />
                </div>
                <div className="contract-card-field contract-card-field-files">
                  <ContractFiles
                    key={`${contract.id}-${refreshKey}`}
                    contractId={contract.id}
                    compact
                    showUpload={false}
                  />
                </div>
              </div>

              {/* Кнопки действий */}
              {canEdit && (
                <div className="contract-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="app-btn app-btn-sm"
                    onClick={() => handleCopyContract(contract)}
                    title="Копировать договор"
                  >
                    <FaCopy />
                  </button>
                  <button
                    type="button"
                    className="app-btn app-btn-sm"
                    onClick={() => setEditingContract(contract)}
                    title="Редактировать"
                  >
                    <FaEdit />
                  </button>
                  <button
                    type="button"
                    className="app-btn app-btn-sm app-btn-danger-outline"
                    onClick={() => setDeleteTarget({ id: contract.id, name: contract.number })}
                    title="Удалить"
                  >
                    <FaTrash />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="empty-state">
          {filterGroup ? 'Нет договоров на этой стадии' : 'Договоры не найдены'}
        </div>
      )}

      <ContractEditModal
        show={!!editingContract}
        onHide={() => setEditingContract(null)}
        contract={editingContract}
        onSaved={() => { setEditingContract(null); loadContracts(true); }}
        onDelete={canEdit && editingContract ? () => {
          setDeleteTarget({ id: editingContract.id, name: editingContract.number });
        } : undefined}
      />

      <ConfirmDeleteModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await apiFetch(`/api/contracts/${deleteTarget.id}/`, { method: 'DELETE' });
          setEditingContract(null);
          setDeleteTarget(null);
          loadContracts(true);
        }}
        title="Удалить договор?"
        message="Будут удалены все стадии, остатки и история этого договора."
        itemName={deleteTarget?.name}
      />

      {/* Модальное окно для создания/копирования договора */}
      <ContractCreateModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setCopyContractData(null);
        }}
        productId={copyContractData?.product} // ID изделия из копируемого договора
        onCreated={handleContractCreated}
        initialData={copyContractData}
      />
    </>
  );
}

export default ContractsList;